import * as React from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { When } from 'react-if';
import { GlobalToken } from 'antd';

import { DeviceConnectivity } from '@/constants/enums';
import { DeviceType } from '@/constants/deviceTypeEnum';
import { useMeasurement } from '@/hooks/useMeasurement';
import { useStores } from '@/hooks/use-stores';
import { useValueByConnectivity } from '@/hooks/useValueByConnectivity';
import AerationBasinWidget from '@/comp/common/icons/AerationBasinWidget';
import { Ports } from '@/comp/MapBuilder/components/Ports';
import { AlertsWidget } from '@/comp/MapBuilder/components/AlertsWidget';
import { getOnDeviceSensorsToDisplay, isSelectedNodeOnMap } from '@/comp/MapBuilder/utils/utils';

export const AerationBasinNodeWidget = observer(({ node, engine = null, width, height, renderedOnMap = false }) => {
  const { tunit, tvalue } = useMeasurement();
  const { tcunit, tcvalue } = useValueByConnectivity();
  const { name } = node.device;
  const { mapBuilderStore } = useStores();

  const isSelected = isSelectedNodeOnMap(node.device.deviceId, mapBuilderStore.selectedDevice?.deviceId, renderedOnMap);

  const getDissolvedOxygenSensorValue = device => {
    return tcvalue(tvalue(device?.dissolvedOxygenPPM, 'ppm'), device?.connectivity?.toLowerCase());
  };

  const [dissolvedOxygenSensors] = getOnDeviceSensorsToDisplay(node?.onDevices, [DeviceType.DissolvedOxygenSensor]);
  const firstDissolvedOxygenSensor = dissolvedOxygenSensors?.[0];

  return (
    <Container width={width} height={height} $mapView={engine ? -1 : 1}>
      <AerationBasinWidget width={width} height={height} isSelected={isSelected} className="background" />

      <When condition={!!engine}>
        <header>
          <h1>{name}</h1>
        </header>

        <section>
          {firstDissolvedOxygenSensor && (
            <div className="measurement-item">
              <div className="text-container">
                <span className="value">{getDissolvedOxygenSensorValue(firstDissolvedOxygenSensor)}</span>
                {firstDissolvedOxygenSensor?.connectivity?.toLowerCase() !== DeviceConnectivity.NoState && (
                  <span className="label">
                    {tcunit(
                      tunit('ppm'),
                      getDissolvedOxygenSensorValue(firstDissolvedOxygenSensor),
                      firstDissolvedOxygenSensor?.connectivity,
                    )}
                  </span>
                )}
              </div>
            </div>
          )}
          <AlertsWidget alerts={node.alerts} />
        </section>

        <Ports node={node} engine={engine} width={width} height={height} />
      </When>
    </Container>
  );
});

interface ContainerProps {
  width: number;
  height: number;
  $mapView: number;
  theme: { token: GlobalToken };
}

const Container = styled.div<ContainerProps>`
  position: relative;
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;

  & :first-child {
    margin-left: ${({ $mapView, theme: { token } }) => ($mapView < 0 ? -token.marginXS : token.marginLG)}px;
  }

  .background {
    position: absolute;
    top: 0;
    left: 0;
    z-index: ${({ $mapView }) => $mapView};
  }

  header {
    padding: ${({ theme: { token } }) => token.paddingXL}px 0 0 ${({ theme: { token } }) => token.paddingMD}px;
    height: 50px;
    display: flex;
    align-items: center;

    h1 {
      font-size: ${({ theme: { token } }) => token.fontSize}px;
      color: ${({ theme: { token } }) => token.colorText};
    }
  }

  section {
    .measurement-item {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 110px;
      margin-top: ${({ theme: { token } }) => token.marginXS}px;
      margin-left: ${({ theme: { token } }) => token.marginXS}px;

      .value {
        font-size: ${({ theme: { token } }) => token.fontSizeHeading3}px;
        font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
      }

      .label {
        font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
        color: ${({ theme: { token } }) => token.colorTextSecondary};
        margin-left: ${({ theme: { token } }) => token.marginXXS}px;
      }
    }
  }
`;
